import React, { useRef, useCallback } from 'react';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

/**
 * Estilos base para la ventana de impresión (documento mínimo para impresora).
 */
const getPrintStyles = () => `
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #000; margin: 12px; padding: 0; }
  .print-header { margin-bottom: 16px; border-bottom: 1px solid #333; padding-bottom: 8px; }
  .print-title { font-size: 20px; font-weight: bold; margin: 0 0 4px 0; }
  .print-empresa-line { font-size: 12px; color: #333; margin: 2px 0; }
  .print-report-title { font-size: 14px; font-weight: 600; color: #333; margin: 8px 0 4px 0; }
  .print-subtitle { font-size: 12px; color: #555; margin: 0; }
  .print-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  .print-table th, .print-table td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
  .print-table th { background: #f0f0f0; font-weight: bold; }
  .print-table tr:nth-child(even) { background: #fafafa; }
  .print-table .align-right { text-align: right; }
  .print-table .align-center { text-align: center; }
  .print-footer { margin-top: 16px; padding-top: 10px; border-top: 1.5px solid #333; font-size: 11px; color: #555; }
  .print-footer-title { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #111; margin: 0 0 8px 0; }
  .print-kpis { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .print-kpis td { border: 1px solid #333; padding: 8px 6px; text-align: center; vertical-align: top; }
  .print-kpis .kpi-label { font-size: 8.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #444; }
  .print-kpis .kpi-value { font-size: 12.5px; font-weight: 700; margin-top: 5px; font-variant-numeric: tabular-nums; color: #000; }
  .print-kpis .kpi-hint { font-size: 9px; color: #666; margin-top: 3px; }
  .print-kpis .kpi-emphasis { background: #f0f0f0; }
  @media print { body { margin: 0; } }
`;

/**
 * Normaliza el objeto de empresa para el encabezado de impresión.
 * Acepta claves en camelCase (config. factura) o snake_case / aliases comunes.
 */
const resolverEmpresaHeader = (empresa, titleFallback) => {
  if (!empresa || typeof empresa !== 'object') {
    return {
      nombre: (titleFallback || '').trim(),
      direccion: '',
      telefono: '',
      rnc: '',
      email: '',
    };
  }
  const nombre = (
    empresa.nombre ||
    empresa.nombreEmpresa ||
    empresa.empresa ||
    empresa.name ||
    empresa.ncomp ||
    titleFallback ||
    ''
  ).trim();
  const direccion = (
    empresa.direccion ||
    empresa.direccionEmpresa ||
    empresa.direccion_empresa ||
    ''
  ).trim();
  const telefono = (
    empresa.telefono ||
    empresa.telefonoEmpresa ||
    empresa.telefono_empresa ||
    ''
  ).trim();
  const rnc = (empresa.rnc || empresa.rncEmpresa || empresa.rnc_empresa || '').trim();
  const email = (empresa.email || empresa.emailEmpresa || empresa.email_empresa || '').trim();
  return { nombre, direccion, telefono, rnc, email };
};

/**
 * HTML del bloque de encabezado (empresa + títulos del reporte).
 */
const buildHeaderHtml = (empresa, title, reportTitle, subtitle) => {
  const emp = resolverEmpresaHeader(empresa, title);
  const tituloPrincipal = emp.nombre || (title || '').trim();
  const lineasEmpresa = [];
  if (emp.direccion) lineasEmpresa.push(emp.direccion);
  if (emp.telefono) lineasEmpresa.push(`Tel: ${emp.telefono}`);
  if (emp.rnc) lineasEmpresa.push(`RNC: ${emp.rnc}`);
  if (emp.email) lineasEmpresa.push(emp.email);

  if (!tituloPrincipal && !reportTitle && !subtitle && lineasEmpresa.length === 0) {
    return '';
  }

  return (
    `<div class="print-header">` +
    (tituloPrincipal ? `<h1 class="print-title">${escapeHtml(tituloPrincipal)}</h1>` : '') +
    lineasEmpresa
      .map((linea) => `<p class="print-empresa-line">${escapeHtml(linea)}</p>`)
      .join('') +
    (reportTitle ? `<p class="print-report-title">${escapeHtml(reportTitle)}</p>` : '') +
    (subtitle ? `<p class="print-subtitle">${escapeHtml(subtitle)}</p>` : '') +
    `</div>`
  );
};

/**
 * Escapa HTML para evitar inyección.
 */
const escapeHtml = (str) => {
  if (str == null) return '';
  const s = String(str);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Genera el HTML de la tabla a partir de columnas y datos.
 */
const buildTableHtml = (columns, data, emptyMessage) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    return `<p>${escapeHtml(emptyMessage || 'Sin columnas definidas')}</p>`;
  }
  const thead = columns
    .map(
      (col) =>
        `<th class="${col.align === 'right' ? 'align-right' : col.align === 'center' ? 'align-center' : ''}">${escapeHtml(col.label)}</th>`
    )
    .join('');
  let tbody = '';
  if (!Array.isArray(data) || data.length === 0) {
    tbody = `<tr><td colspan="${columns.length}">${escapeHtml(emptyMessage || 'No hay datos')}</td></tr>`;
  } else {
    tbody = data
      .map((row) => {
        const celdas = columns
          .map((col) => {
            const raw = row[col.id];
            const value = col.render ? (typeof col.render === 'function' ? col.render(raw, row) : raw) : raw;
            const text = value != null && typeof value !== 'object' ? String(value) : '';
            const alignClass = col.align === 'right' ? 'align-right' : col.align === 'center' ? 'align-center' : '';
            return `<td class="${alignClass}">${escapeHtml(text)}</td>`;
          })
          .join('');
        return `<tr>${celdas}</tr>`;
      })
      .join('');
  }
  return `<table class="print-table"><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>`;
};

/**
 * Componente reutilizable para imprimir listados (envío a impresora).
 *
 * Uso con tabla (columnas + datos):
 *   <PrintListado
 *     title="Facturas emitidas"
 *     subtitle="Del 01/01/2024 al 31/01/2024"
 *     columns={[{ id: 'nfact', label: 'Nº' }, { id: 'total', label: 'Total', align: 'right' }]}
 *     data={facturas}
 *     emptyMessage="No hay facturas"
 *     printButtonLabel="Imprimir listado"
 *   />
 *
 * Uso con contenido personalizado (children):
 *   <PrintListado title="Mi listado" printButtonLabel="Imprimir">
 *     <div ref={ref}>... contenido que quieres imprimir ...</div>
 *   </PrintListado>
 *   Para children, el componente clona el HTML del nodo para la ventana de impresión.
 *
 * @param {string} title - Título principal (ej. nombre de la empresa). Si se pasa `empresa.nombre`, este tiene prioridad.
 * @param {string} [reportTitle] - Título secundario (ej. nombre del reporte).
 * @param {string} [subtitle] - Línea opcional bajo el reporte (ej. rango de fechas).
 * @param {{nombre?: string, direccion?: string, telefono?: string, rnc?: string, email?: string}|null} [empresa] - Datos de empresa para el encabezado de impresión.
 * @param {Array<{id: string, label: string, align?: 'left'|'right'|'center', render?: (value, row) => any}>} [columns] - Columnas de la tabla (usado con data).
 * @param {Array<object>} [data] - Filas de datos (usado con columns).
 * @param {React.ReactNode} [footer] - Pie del listado (modo tabla). Texto plano, o HTML si el string empieza por `<`.
 * @param {string} [emptyMessage] - Mensaje cuando no hay datos.
 * @param {string} [printButtonLabel] - Texto del botón de imprimir.
 * @param {object} [printButtonProps] - Props adicionales para el Button.
 * @param {React.ReactNode} [children] - Contenido personalizado para imprimir (si se usa, tiene prioridad sobre columns/data).
 * @param {React.RefObject<HTMLElement>} [contentRef] - Ref del nodo cuyo HTML se imprimirá (alternativa a children; tiene prioridad sobre children y columns/data).
 * @param {string} [contentId] - Id del contenedor imprimible (para CSS global si se usa window.print en página).
 * @param {string} [bodyHtml] - HTML del cuerpo (documento a medida). Tiene prioridad sobre tabla/children si viene con contenido.
 * @param {string} [extraStyles] - CSS adicional. Si hay bodyHtml, sustituye los estilos de tabla genéricos.
 * @param {boolean} [skipHeader] - Omite el encabezado estándar (cuando bodyHtml ya incluye el de documento).
 */
const PrintListado = ({
  title = 'Listado',
  reportTitle,
  subtitle,
  empresa,
  columns,
  data,
  footer,
  emptyMessage = 'No hay datos para mostrar',
  printButtonLabel = 'Imprimir listado',
  printButtonProps = {},
  children,
  contentRef: contentRefExternal,
  contentId = 'printable-section',
  bodyHtml,
  extraStyles,
  skipHeader = false,
}) => {
  const contentRefInternal = useRef(null);
  const contentRef = contentRefExternal || contentRefInternal;

  const handlePrint = useCallback(() => {
    const usaDocumento = typeof bodyHtml === 'string' && bodyHtml.length > 0;
    const styles = usaDocumento
      ? String(extraStyles || '')
      : `${getPrintStyles()}${extraStyles || ''}`;
    let bodyContent = '';

    if (usaDocumento) {
      bodyContent = bodyHtml;
    } else if (contentRef?.current) {
      bodyContent = contentRef.current.innerHTML;
    } else if (children != null && contentRefInternal.current) {
      bodyContent = contentRefInternal.current.innerHTML;
    } else if (Array.isArray(columns) && columns.length > 0) {
      const tableHtml = buildTableHtml(columns, data, emptyMessage);
      bodyContent = tableHtml;
      if (footer != null) {
        const footerContent =
          typeof footer === 'string'
            ? footer.trim().startsWith('<')
              ? footer.trim()
              : escapeHtml(footer)
            : '';
        if (footerContent) bodyContent += `<div class="print-footer">${footerContent}</div>`;
      }
    } else {
      bodyContent = `<p>${escapeHtml(emptyMessage)}</p>`;
    }

    const headerHtml = skipHeader || usaDocumento ? '' : buildHeaderHtml(empresa, title, reportTitle, subtitle);
    const docTitle = escapeHtml(reportTitle || title || 'Impresión');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${docTitle}</title><style>${styles}</style></head><body>${headerHtml}${bodyContent}</body></html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('No se pudo abrir la ventana de impresión. Permita ventanas emergentes para este sitio.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.afterprint = () => printWindow.close();
      // Cerrar después de imprimir (algunos navegadores no disparan afterprint)
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  }, [
    title,
    reportTitle,
    subtitle,
    empresa,
    columns,
    data,
    emptyMessage,
    footer,
    children,
    contentRef,
    bodyHtml,
    extraStyles,
    skipHeader,
  ]);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          px: 3,
          py: 1.2,
          fontWeight: 600,
          ...printButtonProps.sx,
        }}
        {...printButtonProps}
      >
        {printButtonLabel}
      </Button>

      {children != null && !contentRefExternal && (
        <div id={contentId} ref={contentRefInternal} style={{ display: 'none' }} aria-hidden="true">
          {children}
        </div>
      )}
    </>
  );
};

export default PrintListado;
export { buildTableHtml, getPrintStyles, escapeHtml, buildHeaderHtml, resolverEmpresaHeader };
